import { UserProfile } from '../types';
import { FALLBACK_LNG } from '../locales';

/**
 * Determines the appropriate locale for the user based on a priority cascade:
 * 1. User's personal locale preference (userProfile.locale)
 * 2. Organization's default locale (userProfile.org_locale)
 * 3. Browser/system locale (navigator.language)
 * 4. Fallback to en-US
 * 
 * This follows the localization best practices for locale selection.
 * 
 * @param userProfile - The authenticated user's profile
 * @param supportedLocales - Array of locale codes that the application supports
 * @returns The determined locale code (e.g., 'en', 'zh', 'fr')
 */
export function determineUserLocale(
  userProfile: UserProfile | null,
  supportedLocales: string[]
): string {
  // Priority 1: User's personal locale preference
  if (userProfile?.locale && isLocaleSupported(userProfile.locale, supportedLocales)) {
    console.log(`[Locale] Using user profile locale: ${userProfile.locale}`);
    return normalizeLocale(userProfile.locale);
  }

  // Priority 2: Organization's default locale
  if (userProfile?.org_locale && typeof userProfile.org_locale === 'string' && isLocaleSupported(userProfile.org_locale, supportedLocales)) {
    console.log(`[Locale] Using organization locale: ${userProfile.org_locale}`);
    return normalizeLocale(userProfile.org_locale);
  }

  // Priority 3: Browser/system locale
  const browserLocale = getBrowserLocale();
  if (browserLocale && isLocaleSupported(browserLocale, supportedLocales)) {
    console.log(`[Locale] Using browser locale: ${browserLocale}`);
    return normalizeLocale(browserLocale);
  }

  // Priority 4: Fallback to default (en-US)
  console.log(`[Locale] Using fallback locale: ${FALLBACK_LNG}`);
  return FALLBACK_LNG;
}

/**
 * Gets the browser's locale preference
 * Handles both full locale codes (en-US) and short codes (en)
 */
function getBrowserLocale(): string | null {
  if (typeof navigator === 'undefined') {
    return null;
  }

  // Get the primary language from the browser
  const browserLang = navigator.language || (navigator as any).userLanguage;
  
  if (!browserLang) {
    return null;
  }

  // Extract the primary language code (e.g., 'en' from 'en-US')
  return browserLang.split('-')[0].toLowerCase();
}

/**
 * Normalizes a locale code to the format used by the application
 * Examples: 'en-US' -> 'en', 'zh-CN' -> 'zh'
 */
function normalizeLocale(locale: string): string {
  return locale.split('-')[0].toLowerCase();
}

/**
 * Checks if a locale is supported by the application
 * @param locale - The locale code to check
 * @param supportedLocales - Array of supported locale codes
 * @returns true if the locale is supported
 */
function isLocaleSupported(locale: string, supportedLocales: string[]): boolean {
  const normalizedLocale = normalizeLocale(locale);
  return supportedLocales.includes(normalizedLocale);
}

/**
 * Type guard to check if a locale has MUI support
 * This is useful when you need to ensure MUI components will be localized
 */
export function hasMuiLocaleSupport(
  locale: string,
  muiLocales: Record<string, any>
): boolean {
  const normalizedLocale = normalizeLocale(locale);
  return normalizedLocale in muiLocales;
}

